import { useState } from 'react'
import { FolderInput, GripVertical, Plus, RefreshCw, Sparkles, X } from 'lucide-react'
import type { ActionItem, Dashboard, DashboardCard } from '../types'
import { InsightChart } from '../charts/InsightChart'
import { ChartErrorBoundary } from '../charts/ChartErrorBoundary'
import { formatDayShort, todayIso } from '../format'
import { SearchableSelect } from './SearchableSelect'
import { InfoTip } from './InfoTip'
import { OUT_OF_SCOPE_INFO, OUT_OF_SCOPE_TEXT } from '../cardScope'
import { neutralSavedCard } from '../legacySourceText'

// MP-OS-52: the meeting → action checklist rendered inside an action card.
function ActionChecklist({ items, onChange }: { items: ActionItem[]; onChange: (items: ActionItem[]) => void }) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const text = draft.trim()
    if (!text) return
    onChange([...items, { id: crypto.randomUUID(), text, done: false }])
    setDraft('')
  }
  const patch = (id: string, fields: Partial<ActionItem>) =>
    onChange(items.map((it) => (it.id === id ? { ...it, ...fields } : it)))
  return (
    <div className="action-list">
      <ul>
        {items.map((it) => (
          <li key={it.id} className={it.done ? 'done' : ''}>
            <input type="checkbox" checked={it.done} onChange={() => patch(it.id, { done: !it.done })} aria-label="Klarmarkera" />
            <input className="action-text" value={it.text} aria-label="Action" onChange={(e) => patch(it.id, { text: e.target.value })} />
            <button className="action-remove" onClick={() => onChange(items.filter((x) => x.id !== it.id))} title="Remove" aria-label="Remove action">
              <X size={13} />
            </button>
          </li>
        ))}
      </ul>
      <div className="action-add">
        <input
          value={draft}
          placeholder="Add action…"
          aria-label="New action"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <button onClick={add} aria-label="Add action">
          <Plus size={14} />
        </button>
      </div>
    </div>
  )
}

function fmtDate(iso: string): string {
  try {
    if (!Number.isFinite(Date.parse(iso))) return 'date unknown'
    return formatDayShort(todayIso(new Date(iso)))
  } catch {
    return 'date unknown'
  }
}

// MPD-84 M71-1: the shared SearchableSelect (search, keyboard, a fixed overlay that stays on
// screen), so 15+ boards stay reachable on a phone. Nothing is selected (`value=""`); the folder
// icon is its face.
function MoveMenu({
  dashboards,
  currentId,
  onMove,
}: {
  dashboards: Dashboard[]
  currentId: string
  onMove: (targetId: string) => void
}) {
  const targets = dashboards.filter((d) => d.id !== currentId)
  if (targets.length === 0) return null
  return (
    <SearchableSelect
      className="move-wrap"
      triggerClassName="move-trigger"
      label="Move to another dashboard"
      searchLabel="Search dashboards"
      showAllLabel="Show all dashboards"
      title="Move to another dashboard"
      options={targets.map((d) => ({ value: d.id, label: d.name }))}
      value=""
      placeholder="Move to…"
      onChange={onMove}
      adornment={<FolderInput size={15} aria-hidden />}
    />
  )
}

export function DashboardCardView({
  card: saved,
  dashboards,
  currentId,
  refreshing,
  error,
  onRename,
  onEditText,
  onEditAction,
  onRefresh,
  onReground,
  onRemove,
  onMove,
}: {
  card: DashboardCard
  dashboards: Dashboard[]
  currentId: string
  refreshing: boolean
  error?: string
  onRename: (title: string) => void
  onEditText: (patch: { heading?: string; body?: string }) => void
  onEditAction: (patch: { heading?: string; items?: ActionItem[] }) => void
  onRefresh: () => void
  onReground?: () => void
  onRemove: () => void
  onMove: (targetId: string) => void
}) {
  // MPD-118: the stored answer in the neutral source wording, however the card reached the board.
  const card = neutralSavedCard(saved)
  return (
    <div className={`dash-card ${card.type === 'text' || card.type === 'action' ? 'text-card' : ''}`}>
      <header className="dash-card-head">
        <button type="button" className="drag-handle" aria-label="Move card with arrow keys" title="Move card · drag or use arrow keys">
          <GripVertical size={16} />
        </button>
        {card.type === 'chart' ? (
          <input
            className="card-title"
            value={card.title}
            aria-label="Card title"
            onChange={(e) => onRename(e.target.value)}
          />
        ) : (
          <input
            className="card-title"
            value={card.heading}
            placeholder="Heading"
            aria-label="Heading"
            onChange={(e) =>
              card.type === 'action'
                ? onEditAction({ heading: e.target.value })
                : onEditText({ heading: e.target.value })
            }
          />
        )}
        <div className="card-actions">
          {card.type === 'chart' && (
            <button onClick={onRefresh} title={card.spec ? 'Update the data' : onReground ? 'Saved analysis — use Run question again to update' : 'Saved analysis'} aria-label="Update card data" disabled={refreshing || !card.spec}>
              <RefreshCw size={15} className={refreshing ? 'spin' : ''} />
            </button>
          )}
          {card.type === 'chart' && onReground && (
            <button
              onClick={onReground}
              title="Run question again"
              aria-label="Re-run the question via AI"
              disabled={refreshing}
            >
              <Sparkles size={15} />
            </button>
          )}
          <MoveMenu dashboards={dashboards} currentId={currentId} onMove={onMove} />
          <button onClick={onRemove} aria-label="Remove card" title="Remove card" className="danger">
            <X size={15} />
          </button>
        </div>
      </header>

      <div className="dash-card-body">
        {card.type === 'chart' && card.out_of_scope ? (
          // MPD-84 N1: never the stored figures; the card can still be deleted.
          <p className="stat">
            {OUT_OF_SCOPE_TEXT}
            <InfoTip label="About this card" size="small">{OUT_OF_SCOPE_INFO}</InfoTip>
          </p>
        ) : card.type === 'chart' ? (
          card.last_contract.chart ? (
            <>
              <p className="card-summary">{card.last_contract.answer}</p>
              <ChartErrorBoundary><InsightChart chart={card.last_contract.chart} fill /></ChartErrorBoundary>
            </>
          ) : (
            <p className="stat">{card.last_contract.answer}</p>
          )
        ) : card.type === 'action' ? (
          <ActionChecklist items={card.items} onChange={(items) => onEditAction({ items })} />
        ) : (
          <textarea
            className="card-text"
            value={card.body}
            placeholder="Write a comment…"
            aria-label="Text"
            onChange={(e) => onEditText({ body: e.target.value })}
          />
        )}
        {/* The limits that change how the figures read stay visible; source and definitions
            (caveat_info, MPD-67) sit behind the ⓘ. A card pinned before the split has only
            `caveat` and shows it as before; one with only `caveat_info` gets the ⓘ, no box. */}
        {card.type === 'chart' && card.out_of_scope ? null : card.type === 'chart' && card.last_contract.caveat ? (
          <div className="callout small">
            <p>
              {card.last_contract.caveat}
              {card.last_contract.caveat_info && (
                <InfoTip label="About this answer's figures" size="small">{card.last_contract.caveat_info}</InfoTip>
              )}
            </p>
          </div>
        ) : card.type === 'chart' && card.last_contract.caveat_info ? (
          <p className="card-caveat-info">
            <InfoTip label="About this answer's figures" size="small">{card.last_contract.caveat_info}</InfoTip>
          </p>
        ) : null}
        {error && <p className="card-refresh-error" role="alert">{error}</p>}
      </div>

      {card.type === 'chart' && !card.out_of_scope && (
        <footer className="dash-card-foot">
          {card.last_contract.preliminary && (
            <span>
              <span className="prelim-badge">Preliminary</span>
              <InfoTip label="About preliminary figures" size="small">Contains an ongoing period — figures may change.</InfoTip>
            </span>
          )}
          {!card.spec && <span>
            Saved analysis
            <InfoTip label="About saved analysis" size="small">
              This analysis combines data or calculations. Run the question again to update the complete answer.
            </InfoTip>
          </span>}
          <span>{refreshing ? 'Updating…' : card.last_contract.stale
            ? `Last available data · ${card.last_contract.data_as_of ?? fmtDate(card.pinned_at)}`
            : `Updated ${fmtDate(card.pinned_at)}`}</span>
        </footer>
      )}
    </div>
  )
}
